<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div v-if="pageLoading" class="ap-card ap-loading">
          <div class="ap-spinner"></div>
          <span>Loading item...</span>
        </div>

        <div v-else-if="loadFailed" class="ap-card ap-loading">
          <strong>Item could not be loaded</strong>
          <span>It may have been deleted, or the link is incorrect.</span>
          <router-link to="/productlist" class="btn btn-submit mt-2">Back to items</router-link>
        </div>

        <div v-else class="ap-layout">
          <!-- ═══════════ FORM ═══════════ -->
          <div class="ap-main">

            <!-- 1 · Basic information -->
            <section class="ap-card">
              <header class="ap-card-head">
                <span class="ap-step" :class="{ done: sectionDone.basic }">
                  <i v-if="sectionDone.basic" class="fas fa-check"></i><template v-else>1</template>
                </span>
                <div>
                  <h6>Basic information</h6>
                  <p>What the item is called and how you find it</p>
                </div>
              </header>

              <div class="row g-3">
                <div class="col-md-8">
                  <label class="form-label">Item name <span class="req">*</span></label>
                  <b-form-input
                    v-model="form.itemname"
                    ref="itemNameInput"
                    type="text"
                    placeholder="e.g. Tata Tea Premium 500g"
                    :class="{ 'is-invalid': errors.itemname }"
                    @input="clearError('itemname')"
                    @keypress.enter="focusNext('itemcodeInput')"
                  />
                  <div v-if="errors.itemname" class="invalid-feedback d-block">{{ errors.itemname }}</div>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Category <span class="req">*</span></label>
                  <model-list-select
                    ref="categorySelect"
                    v-model="form.itemcategory"
                    :list="categoryListFromApi"
                    option-value="categoryid"
                    option-text="categoryname"
                    placeholder="Select category"
                    :class="{ 'is-invalid': errors.itemcategory }"
                    @update:modelValue="clearError('itemcategory')"
                  />
                  <div v-if="errors.itemcategory" class="invalid-feedback d-block">{{ errors.itemcategory }}</div>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Item code <span class="req">*</span></label>
                  <div class="ap-input-action">
                    <b-form-input
                      v-model="form.itemcode"
                      ref="itemcodeInput"
                      type="text"
                      placeholder="e.g. IT-0001"
                      :class="{ 'is-invalid': errors.itemcode }"
                      @input="onItemCodeInput"
                      @blur="checkItemCodeAvailability"
                      @keypress.enter="focusNext('barcodeInput')"
                    />
                    <button v-if="!isEdit" type="button" class="ap-inline-btn" title="Generate a unique code" @click="generateItemCode">
                      <i class="fas fa-magic"></i> Auto
                    </button>
                  </div>
                  <div v-if="errors.itemcode" class="invalid-feedback d-block">{{ errors.itemcode }}</div>
                  <div v-else-if="itemCodeLookup.status" class="ap-status" :class="itemCodeLookup.status">
                    <i class="fas" :class="statusIcon(itemCodeLookup.status)"></i> {{ itemCodeLookup.message }}
                  </div>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Barcode <span class="opt">optional</span></label>
                  <div class="ap-icon-input">
                    <i class="fas fa-barcode"></i>
                    <b-form-input v-model="form.barcode" ref="barcodeInput" type="text" placeholder="Scan or type barcode" />
                  </div>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Rack / shelf <span class="opt">optional</span></label>
                  <div class="ap-icon-input">
                    <i class="fas fa-th-large"></i>
                    <b-form-input v-model="form.rackno" type="text" placeholder="e.g. A1, Rack-02" />
                  </div>
                </div>

                <div class="col-12">
                  <button v-if="!showDescription && !form.itemdescription" type="button" class="ap-link-btn" @click="showDescription = true">
                    <i class="fas fa-plus"></i> Add a description
                  </button>
                  <template v-else>
                    <label class="form-label">Description <span class="opt">optional</span></label>
                    <b-form-textarea v-model="form.itemdescription" rows="2" max-rows="4" placeholder="Brief description of the item..." />
                  </template>
                </div>
              </div>
            </section>

            <!-- 2 · Pricing -->
            <section class="ap-card">
              <header class="ap-card-head">
                <span class="ap-step" :class="{ done: sectionDone.pricing }">
                  <i v-if="sectionDone.pricing" class="fas fa-check"></i><template v-else>2</template>
                </span>
                <div>
                  <h6>Pricing</h6>
                  <p>Only the selling price is required</p>
                </div>
              </header>

              <div class="row g-3">
                <div class="col-sm-6 col-lg-3">
                  <label class="form-label">Selling price <span class="req">*</span></label>
                  <div class="ap-money" :class="{ 'is-invalid': errors.unitprice }">
                    <span>₹</span>
                    <b-form-input
                      v-model="form.unitprice"
                      ref="unitpriceInput"
                      type="number" min="0" step="0.01" placeholder="0.00"
                      :class="{ 'is-invalid': errors.unitprice }"
                      @input="clearError('unitprice')"
                    />
                  </div>
                  <div v-if="errors.unitprice" class="invalid-feedback d-block">{{ errors.unitprice }}</div>
                </div>
                <div class="col-sm-6 col-lg-3">
                  <label class="form-label">Purchase rate <span class="opt">optional</span></label>
                  <div class="ap-money">
                    <span>₹</span>
                    <b-form-input v-model="form.purchaserate" type="number" min="0" step="0.01" placeholder="0.00" />
                  </div>
                </div>
                <div class="col-sm-6 col-lg-3">
                  <label class="form-label">MRP <span class="opt">optional</span></label>
                  <div class="ap-money">
                    <span>₹</span>
                    <b-form-input v-model="form.mrp" type="number" min="0" step="0.01" placeholder="0.00" />
                  </div>
                </div>
                <div class="col-sm-6 col-lg-3">
                  <label class="form-label">Wholesale rate <span class="opt">optional</span></label>
                  <div class="ap-money">
                    <span>₹</span>
                    <b-form-input v-model="form.wholesalerate" type="number" min="0" step="0.01" placeholder="0.00" />
                  </div>
                </div>
              </div>

              <div v-if="priceWarnings.length" class="ap-hints">
                <div v-for="warning in priceWarnings" :key="warning" class="ap-hint warn">
                  <i class="fas fa-exclamation-triangle"></i> {{ warning }}
                </div>
              </div>
            </section>

            <!-- 3 · Stock -->
            <section class="ap-card">
              <header class="ap-card-head">
                <span class="ap-step" :class="{ done: sectionDone.stock }">
                  <i v-if="sectionDone.stock" class="fas fa-check"></i><template v-else>3</template>
                </span>
                <div>
                  <h6>Stock</h6>
                  <p>{{ isEdit ? "Unit of sale, current quantity and low-stock alert" : "Unit of sale, opening quantity and low-stock alert" }}</p>
                </div>
              </header>

              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">Unit <span class="req">*</span></label>
                  <div class="ap-unit-row" :class="{ 'is-invalid': errors.unit }">
                    <model-list-select
                      ref="unitSelect"
                      v-model="form.unit"
                      :list="unitListFromApi"
                      option-value="unitname"
                      option-text="unitname"
                      placeholder="Select unit"
                      :class="{ 'is-invalid': errors.unit }"
                      @update:modelValue="clearError('unit')"
                    />
                    <button type="button" class="ap-inline-btn" title="Add a new unit" @click="openUnitModal">
                      <i class="fas fa-plus"></i> New
                    </button>
                  </div>
                  <div v-if="errors.unit" class="invalid-feedback d-block">{{ errors.unit }}</div>
                  <div v-else-if="quickUnits.length && !form.unit" class="ap-chips">
                    <button v-for="unit in quickUnits" :key="unit" type="button" class="ap-chip" @click="form.unit = unit">{{ unit }}</button>
                  </div>
                </div>
                <div class="col-sm-6 col-md-4">
                  <label class="form-label">{{ isEdit ? "Current stock" : "Opening stock" }} <span v-if="!isEdit" class="req">*</span></label>
                  <div class="ap-suffix">
                    <b-form-input
                      v-model="form.available"
                      ref="availableInput"
                      type="number" min="0" step="0.01" placeholder="0"
                      :readonly="isEdit"
                      :class="{ 'is-invalid': errors.available, 'ap-readonly': isEdit }"
                      @input="clearError('available')"
                    />
                    <span>{{ form.unit || "qty" }}</span>
                  </div>
                  <div v-if="errors.available" class="invalid-feedback d-block">{{ errors.available }}</div>
                  <small v-else-if="isEdit" class="ap-help"><i class="fas fa-lock"></i> Changes only through purchases, sales and returns</small>
                  <small v-else class="ap-help">Quantity you have right now</small>
                </div>
                <div class="col-sm-6 col-md-4">
                  <label class="form-label">Low-stock alert at <span class="req">*</span></label>
                  <div class="ap-suffix">
                    <b-form-input
                      v-model="form.minstock"
                      ref="minstockInput"
                      type="number" min="0" placeholder="e.g. 5"
                      :class="{ 'is-invalid': errors.minstock }"
                      @input="clearError('minstock')"
                    />
                    <span>{{ form.unit || "qty" }}</span>
                  </div>
                  <div v-if="errors.minstock" class="invalid-feedback d-block">{{ errors.minstock }}</div>
                  <small v-else class="ap-help">You'll be warned when stock falls to this level</small>
                </div>
              </div>

              <!-- Packing units -->
              <div class="ap-sub" :class="{ open: showPacking }">
                <button type="button" class="ap-sub-toggle" :aria-expanded="showPacking" @click="showPacking = !showPacking">
                  <i class="fas fa-box-open"></i>
                  <span>
                    <strong>Packing units</strong>
                    <small>{{ packingSummary || "Optional — e.g. 1 Box = 12 Pieces" }}</small>
                  </span>
                  <i class="fas fa-chevron-down ap-chevron"></i>
                </button>
                <div v-if="showPacking" class="ap-packing">
                  <div class="ap-packing-row">
                    <span class="ap-packing-one">1</span>
                    <b-form-input v-model="form.uom_upper" type="text" placeholder="Box / Carton" class="ap-packing-name" />
                    <span class="ap-packing-eq">=</span>
                    <b-form-input v-model="form.conversionfactor" type="number" min="1" step="1" placeholder="12" class="ap-packing-qty" />
                    <b-form-input v-model="form.uom_lower" type="text" placeholder="Piece / Bottle" class="ap-packing-name" />
                  </div>
                  <small class="ap-help">Use this if you buy in bulk packs but sell loose units.</small>
                </div>
              </div>
            </section>

            <!-- 4 · Tax -->
            <section ref="taxSection" class="ap-card">
              <header class="ap-card-head">
                <span class="ap-step" :class="{ done: sectionDone.tax }">
                  <i v-if="sectionDone.tax" class="fas fa-check"></i><template v-else>4</template>
                </span>
                <div>
                  <h6>Tax</h6>
                  <p>GST rate is picked up from the HSN code</p>
                </div>
              </header>

              <label class="form-label">Tax type</label>
              <div class="ap-segment" role="radiogroup" aria-label="Tax type">
                <button
                  v-for="option in taxTypeOptions"
                  :key="option.value"
                  type="button"
                  role="radio"
                  :aria-checked="form.taxtype === option.value"
                  :class="{ active: form.taxtype === option.value }"
                  @click="setTaxType(option.value)"
                >
                  {{ option.text }}
                </button>
              </div>

              <template v-if="isTaxableType">
                <div class="row g-3 mt-1">
                  <div class="col-md-6">
                    <template v-if="!form.is_manual_tax_rate">
                      <label class="form-label">{{ codeLabel }} code <span class="req">*</span></label>
                      <div class="ap-input-action">
                        <b-form-input
                          ref="hsnInput"
                          v-model.trim="form.hsncode"
                          type="text"
                          placeholder="e.g. 09024090"
                          :class="{ 'is-invalid': errors.hsncode }"
                          @blur="resolveTaxRateForCode"
                          @keyup.enter="resolveTaxRateForCode"
                          @input="clearError('hsncode')"
                        />
                        <button type="button" class="ap-inline-btn" @click="resolveTaxRateForCode">Check</button>
                      </div>
                      <div v-if="errors.hsncode" class="invalid-feedback d-block">{{ errors.hsncode }}</div>
                      <div v-if="taxRateLookup.status" class="ap-status" :class="taxRateLookup.status">
                        <i class="fas" :class="statusIcon(taxRateLookup.status)"></i> {{ taxRateLookup.message }}
                      </div>
                      <div class="ap-tax-links">
                        <button v-if="taxRateLookup.status === 'missing'" type="button" class="ap-link-btn" @click="openTaxRateModal">
                          <i class="fas fa-plus"></i> Add {{ codeLabel }} rate
                        </button>
                        <button type="button" class="ap-link-btn muted" @click="enableManualTax">
                          Don't have the {{ codeLabel }} code? Pick the rate manually
                        </button>
                      </div>
                    </template>

                    <template v-else>
                      <label class="form-label">GST rate <span class="req">*</span></label>
                      <div class="ap-rates">
                        <button
                          v-for="rate in gstRates"
                          :key="rate.label"
                          type="button"
                          :class="{ active: selectedGstRate === rate.label }"
                          @click="applyGstRate(rate)"
                        >
                          {{ rate.label }}
                        </button>
                        <button type="button" :class="{ active: selectedGstRate === 'custom' }" @click="selectedGstRate = 'custom'">Other</button>
                      </div>
                      <div v-if="selectedGstRate === 'custom'" class="ap-suffix mt-2" style="max-width: 160px;">
                        <b-form-input
                          v-model="form.taxpercent"
                          type="number" min="0" max="100" step="0.01" placeholder="0"
                          :class="{ 'is-invalid': errors.taxpercent }"
                          @input="syncTaxFromTotal(); clearError('taxpercent')"
                        />
                        <span>%</span>
                      </div>
                      <div v-if="errors.taxpercent" class="invalid-feedback d-block">{{ errors.taxpercent }}</div>
                      <div class="ap-tax-links">
                        <button type="button" class="ap-link-btn muted" @click="disableManualTax">
                          Use {{ codeLabel }} code instead
                        </button>
                      </div>
                    </template>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Selling price is</label>
                    <div class="ap-segment ap-segment-sm">
                      <button type="button" :class="{ active: form.istaxinclusive }" @click="form.istaxinclusive = true">Including tax</button>
                      <button type="button" :class="{ active: !form.istaxinclusive }" @click="form.istaxinclusive = false">Plus tax</button>
                    </div>
                    <small class="ap-help">
                      {{ form.istaxinclusive ? "Tax is already inside the selling price (usual for MRP items)." : "Tax will be added on top of the selling price at billing." }}
                    </small>
                  </div>
                </div>

                <div v-if="taxPercentValue > 0" class="ap-tax-split">
                  <span><small>CGST</small>{{ formatPercent(form.cgstrate) }}</span>
                  <span class="ap-tax-plus">+</span>
                  <span><small>SGST</small>{{ formatPercent(form.sgstrate) }}</span>
                  <span class="ap-tax-or">or</span>
                  <span><small>IGST (other state)</small>{{ formatPercent(form.igstrate) }}</span>
                </div>
              </template>

              <div v-else class="ap-hint info mt-3">
                <i class="fas fa-info-circle"></i>
                <span v-if="form.taxtype === 'EXEMPT'">Exempt from GST — no tax will be charged on this item.</span>
                <span v-else>Outside GST (e.g. petrol, alcohol) — no GST will be charged.</span>
              </div>
            </section>

            <!-- 5 · Photos -->
            <section class="ap-card">
              <header class="ap-card-head">
                <span class="ap-step muted"><i class="fas fa-image"></i></span>
                <div>
                  <h6>Photos <span class="opt">optional</span></h6>
                  <p>Shown on the item page and online store</p>
                </div>
              </header>

              <input
                ref="imageUploader"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                multiple
                style="display: none;"
                @change="handleImageUpload"
              />
              <div
                class="ap-drop"
                :class="{ dragging: isDragging }"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="onDropImages"
              >
                <div v-for="(image, index) in localImages" :key="image.url" class="ap-thumb">
                  <img :src="image.url" alt="Item photo" />
                  <span v-if="index === 0" class="ap-thumb-tag">Main</span>
                  <button type="button" class="ap-thumb-remove" aria-label="Remove photo" @click="removeLocalImage(index)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <button v-if="localImages.length < maxImages" type="button" class="ap-drop-add" @click="$refs.imageUploader.click()">
                  <i class="fas fa-cloud-upload-alt"></i>
                  <span>{{ localImages.length ? "Add more" : "Click or drop photos here" }}</span>
                </button>
              </div>
              <small v-if="uploadError" class="text-danger d-block mt-1">{{ uploadError }}</small>
              <small v-else class="ap-help">JPG, PNG or WEBP, up to 2 MB each. The first photo is the main one.</small>
            </section>

            <!-- Online store (edit only) -->
            <section v-if="isEdit && isEcommerceStoreEnabled" class="ap-card">
              <header class="ap-card-head">
                <span class="ap-step muted"><i class="fas fa-store"></i></span>
                <div class="flex-grow-1">
                  <h6>Online store</h6>
                  <p>How this item appears in your online catalogue. Saved separately from the item.</p>
                </div>
                <span class="ap-online-badge" :class="{ on: ecommerceForm.is_online }">
                  {{ ecommerceForm.is_online ? "Published" : "Not published" }}
                </span>
              </header>

              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">Show in online store</label>
                  <div class="ap-segment ap-segment-sm">
                    <button type="button" :class="{ active: ecommerceForm.is_online }" @click="ecommerceForm.is_online = true">Yes</button>
                    <button type="button" :class="{ active: !ecommerceForm.is_online }" @click="ecommerceForm.is_online = false">No</button>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Online price <span class="opt">optional</span></label>
                  <div class="ap-money">
                    <span>₹</span>
                    <b-form-input v-model="ecommerceForm.online_price" type="number" min="0" step="0.01" :placeholder="form.unitprice || '0.00'" />
                  </div>
                  <small class="ap-help">Leave empty to use the selling price</small>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Web address (slug)</label>
                  <b-form-input v-model="ecommerceForm.slug" placeholder="red-notebook-a4" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Short description</label>
                  <b-form-input v-model="ecommerceForm.short_description" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Online categories</label>
                  <select v-model="ecommerceForm.category_ids" class="form-control ap-multi" multiple>
                    <option v-for="category in ecommerceCategories" :key="category.ecommerce_category_id" :value="category.ecommerce_category_id">
                      {{ category.name }}
                    </option>
                  </select>
                  <small class="ap-help">Hold Ctrl to pick more than one</small>
                </div>
                <div class="col-12">
                  <label class="form-label">Long description</label>
                  <b-form-textarea v-model="ecommerceForm.long_description" rows="3" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">SEO title</label>
                  <b-form-input v-model="ecommerceForm.seo_title" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">SEO description</label>
                  <b-form-input v-model="ecommerceForm.seo_description" />
                </div>
              </div>

              <div class="ap-online-actions">
                <button type="button" class="btn btn-submit" :disabled="onlineSaving" @click="saveOnlineProduct">
                  <i class="fas fa-cloud-upload-alt me-2"></i>{{ onlineSaving ? "Saving..." : "Save online details" }}
                </button>
                <button v-if="ecommerceForm.is_online" type="button" class="btn ap-btn-light" :disabled="onlineSaving" @click="removeFromOnlineStore">
                  <i class="fas fa-store-slash me-2"></i>Remove from online store
                </button>
              </div>
            </section>
          </div>

          <!-- ═══════════ SUMMARY ═══════════ -->
          <aside class="ap-side">
            <div class="ap-summary">
              <div class="ap-preview">
                <div class="ap-preview-img">
                  <img v-if="localImages.length" :src="localImages[0].url" alt="" />
                  <span v-else>{{ previewInitials }}</span>
                </div>
                <div class="ap-preview-text">
                  <strong>{{ form.itemname || (isEdit ? "Item" : "New item") }}</strong>
                  <small>
                    {{ form.itemcode || "No code" }}<template v-if="categoryName"> · {{ categoryName }}</template>
                  </small>
                </div>
              </div>

              <dl class="ap-figures">
                <div><dt>Selling price</dt><dd>{{ money(form.unitprice) }}</dd></div>
                <div v-if="isTaxableType && taxPercentValue > 0">
                  <dt>{{ form.istaxinclusive ? "Tax inside price" : "Tax added" }} ({{ formatPercent(form.taxpercent) }})</dt>
                  <dd>{{ money(priceWithTax) }}</dd>
                </div>
                <div class="ap-figure-strong"><dt>Customer pays</dt><dd>{{ money(customerPays) }}</dd></div>
                <div v-if="margin">
                  <dt>Margin per {{ form.unit || "unit" }}</dt>
                  <dd :class="margin.amount < 0 ? 'neg' : 'pos'">{{ money(margin.amount) }} <small>({{ margin.percent }}%)</small></dd>
                </div>
                <div v-if="stockValue > 0"><dt>{{ isEdit ? "Stock value" : "Opening stock value" }}</dt><dd>{{ money(stockValue) }}</dd></div>
              </dl>

              <div class="ap-checklist">
                <div class="ap-checklist-head">
                  <span>Required fields</span>
                  <span>{{ checklistDone }}/{{ checklist.length }}</span>
                </div>
                <div class="ap-progress"><span :style="{ width: `${(checklistDone / checklist.length) * 100}%` }"></span></div>
                <ul>
                  <li v-for="check in checklist" :key="check.label" :class="{ ok: check.ok }">
                    <i class="fas" :class="check.ok ? 'fa-check-circle' : 'fa-circle'"></i>
                    <button type="button" @click="goToField(check.focus)">{{ check.label }}</button>
                  </li>
                </ul>
              </div>

              <div class="ap-actions">
                <button type="button" class="btn btn-submit w-100" :disabled="saving || (isEdit && !isDirty)" @click="postData()">
                  <i class="fas fa-check me-2"></i>{{ saving ? "Saving..." : isEdit ? "Save changes" : "Save item" }}
                </button>
                <button v-if="!isEdit" type="button" class="btn ap-btn-light w-100" :disabled="saving" @click="postData({ addAnother: true })">
                  <i class="fas fa-plus me-2"></i>Save &amp; add another
                </button>
                <small v-if="isEdit" class="ap-dirty" :class="{ on: isDirty }">
                  <i class="fas" :class="isDirty ? 'fa-circle' : 'fa-check'"></i>
                  {{ isDirty ? "You have unsaved changes" : "No changes yet" }}
                </small>
                <router-link to="/productlist" class="ap-cancel">Cancel</router-link>
                <small class="ap-kbd">Tip: press <kbd>Ctrl</kbd> + <kbd>S</kbd> to save</small>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>

    <div
      v-if="showUnitModal"
      class="quick-create-modal-backdrop"
      @click.self="closeUnitModal"
    >
      <div class="quick-create-modal" role="dialog" aria-modal="true" aria-labelledby="unit-quick-create-title">
        <div class="quick-create-modal-header">
          <div>
            <h5 id="unit-quick-create-title" class="quick-create-modal-title">Add New Unit</h5>
            <p class="quick-create-modal-subtitle">The unit will be available immediately in this form.</p>
          </div>
          <button type="button" class="quick-create-close" @click="closeUnitModal" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Unit Name <span class="required-star">*</span></label>
            <b-form-input
              ref="unitModalNameInput"
              v-model="unitModalForm.unitname"
              type="text"
              placeholder="e.g. Nos, Kg, Box"
              @keyup.enter="createUnitFromModal"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Unit Code</label>
            <b-form-input
              v-model="unitModalForm.unitcode"
              type="text"
              placeholder="Optional short code"
              @keyup.enter="createUnitFromModal"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Description</label>
            <b-form-textarea
              v-model="unitModalForm.description"
              rows="2"
              placeholder="Optional description"
            />
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-light" @click="closeUnitModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="unitModalSaving" @click="createUnitFromModal">
            {{ unitModalSaving ? "Saving..." : "Save Unit" }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showTaxRateModal"
      class="quick-create-modal-backdrop"
      @click.self="closeTaxRateModal"
    >
      <div class="quick-create-modal" role="dialog" aria-modal="true" aria-labelledby="tax-rate-quick-create-title">
        <div class="quick-create-modal-header">
          <div>
            <h5 id="tax-rate-quick-create-title" class="quick-create-modal-title">Add {{ taxRateModalForm.code_type }} Rate</h5>
            <p class="quick-create-modal-subtitle">This code will be available immediately for the item.</p>
          </div>
          <button type="button" class="quick-create-close" @click="closeTaxRateModal" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">Code Type</label>
            <b-form-input v-model="taxRateModalForm.code_type" type="text" readonly />
          </div>
          <div class="col-md-8">
            <label class="form-label">Code</label>
            <b-form-input v-model="taxRateModalForm.code" type="text" readonly />
          </div>
          <div class="col-md-6">
            <label class="form-label">Tax Percent <span class="required-star">*</span></label>
            <b-form-input
              ref="taxRatePercentInput"
              v-model.number="taxRateModalForm.tax_percent"
              type="number"
              min="0"
              max="100"
              step="0.01"
              placeholder="e.g. 18"
              @keyup.enter="createTaxRateFromModal"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Effective From <span class="required-star">*</span></label>
            <b-form-input
              v-model="taxRateModalForm.effective_from"
              type="date"
              @keyup.enter="createTaxRateFromModal"
            />
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-light" @click="closeTaxRateModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="taxRateModalSaving" @click="createTaxRateFromModal">
            {{ taxRateModalSaving ? "Saving..." : "Save Tax Rate" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ModelListSelect } from "vue-search-select";
import axios from "axios";

async function ImageUploadApi(item_id, files) {
  const formData = new FormData();
  files.forEach((file) => formData.append("image_files", file));
  const response = await axios.post(`/v1/items/${item_id}/images`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

export default {
  name: "ItemForm",
  components: { ModelListSelect },
  props: {
    mode: {
      type: String,
      default: "add",
      validator: (value) => ["add", "edit"].includes(value),
    },
  },

  data() {
    const isEdit = this.mode === "edit";
    return {
      title: isEdit ? "Edit Item" : "Add Item",
      title1: isEdit ? "Update an existing item" : "Create a new inventory item",
      itemId: isEdit ? this.$route.query.id || null : null,
      pageLoading: isEdit,
      loadFailed: false,
      savedSnapshot: "",
      originalItemCode: "",
      imagesToDelete: [],
      onlineSaving: false,
      ecommerceCategories: [],
      ecommerceForm: {
        is_online: false,
        online_price: "",
        slug: "",
        short_description: "",
        long_description: "",
        seo_title: "",
        seo_description: "",
        category_ids: [],
      },

      form: {
        // Basic
        itemname: "",
        itemdescription: "",
        itemcategory: "",
        itemcode: "",
        barcode: "",
        rackno: "",

        // Pricing
        unitprice: "",
        purchaserate: "",
        mrp: "",
        wholesalerate: "",

        // Stock
        unit: "",
        available: "",
        threshold: "",
        minstock: "",
        maxstock: "",

        // Tax
        hsncode: "",
        taxpercent: "",
        cgstrate: "",
        sgstrate: "",
        igstrate: "",
        is_manual_tax_rate: false,
        taxtype: "GST",
        istaxinclusive: true,

        // ── TRACKING OPTIONS (disabled — future feature) ──
        // batchenabled: false,
        // expiryenabled: false,

        // UOM
        uom_upper: "",
        uom_lower: "",
        conversionfactor: "",
      },

      errors: {},

      categoryListFromApi: [],
      unitListFromApi: [],
      showUnitModal: false,
      unitModalSaving: false,
      unitModalForm: {
        unitname: "",
        unitcode: "",
        description: "",
      },
      itemCodeLookup: {
        status: "",
        message: "",
      },
      itemCodeCheckTimer: null,
      itemCodeChecking: false,
      showTaxRateModal: false,
      taxRateModalSaving: false,
      taxRateModalForm: {
        code_type: "HSN",
        code: "",
        tax_percent: "",
        effective_from: "",
      },

      // Tax type options
      taxTypeOptions: [
        { value: "GST", text: "GST (Goods)" },
        { value: "SAC", text: "SAC (Services)" },
        { value: "EXEMPT", text: "Exempt" },
        { value: "NONGST", text: "Non-GST" }
      ],

      // GST quick-select rates (standard Indian GST slabs)
      gstRates: [
        { label: "0%",  total: 0,  cgst: 0,   sgst: 0,   igst: 0   },
        { label: "5%",  total: 5,  cgst: 2.5, sgst: 2.5, igst: 5   },
        { label: "12%", total: 12, cgst: 6,   sgst: 6,   igst: 12  },
        { label: "18%", total: 18, cgst: 9,   sgst: 9,   igst: 18  },
        { label: "28%", total: 28, cgst: 14,  sgst: 14,  igst: 28  },
      ],
      selectedGstRate: null,
      taxRateLookup: {
        status: "",
        message: "",
      },
      localImages: [],
      maxImages: 20,
      uploadError: "",
      saving: false,
      showDescription: false,
      showPacking: false,
      isDragging: false,

      // ── IMAGE UPLOAD (disabled — future feature) ──
      // localImages: [],
      // maxImages: 4,
      // uploadError: "",
    };
  },

  computed: {
    isEdit() {
      return this.mode === "edit";
    },
    isEcommerceStoreEnabled() {
      return this.$store.getters.isEcommerceStoreEnabled;
    },
    isDirty() {
      if (!this.isEdit) return true;
      return this.snapshot() !== this.savedSnapshot;
    },
    isTaxableType() {
      return ["GST", "SAC"].includes(this.form.taxtype);
    },
    codeLabel() {
      return this.form.taxtype === "SAC" ? "SAC" : "HSN";
    },
    taxPercentValue() {
      return this.isTaxableType ? Number(this.form.taxpercent) || 0 : 0;
    },
    categoryName() {
      const match = this.categoryListFromApi.find((category) => category.categoryid === this.form.itemcategory);
      return match ? match.categoryname : "";
    },
    previewInitials() {
      const words = String(this.form.itemname || "").trim().split(/\s+/).filter(Boolean);
      return words.length ? words.slice(0, 2).map((word) => word[0].toUpperCase()).join("") : "?";
    },
    sellingExTax() {
      const price = Number(this.form.unitprice) || 0;
      const tax = this.taxPercentValue;
      return this.form.istaxinclusive && tax > 0 ? (price * 100) / (100 + tax) : price;
    },
    customerPays() {
      const price = Number(this.form.unitprice) || 0;
      return this.form.istaxinclusive ? price : price + Number(this.priceWithTax || 0);
    },
    margin() {
      const purchase = Number(this.form.purchaserate) || 0;
      if (!purchase || !(Number(this.form.unitprice) > 0)) return null;
      const amount = this.sellingExTax - purchase;
      const percent = this.sellingExTax ? ((amount / this.sellingExTax) * 100).toFixed(1) : "0";
      return { amount, percent };
    },
    stockValue() {
      return (Number(this.form.available) || 0) * (Number(this.form.purchaserate) || 0);
    },
    priceWarnings() {
      const price = Number(this.form.unitprice) || 0;
      const mrp = Number(this.form.mrp) || 0;
      const wholesale = Number(this.form.wholesalerate) || 0;
      const warnings = [];
      if (price > 0 && mrp > 0 && price > mrp) warnings.push("Selling price is higher than MRP.");
      if (this.margin && this.margin.amount < 0) warnings.push("Selling price (before tax) is below the purchase rate. You will make a loss.");
      if (price > 0 && wholesale > price) warnings.push("Wholesale rate is higher than the selling price.");
      return warnings;
    },
    quickUnits() {
      return (this.unitListFromApi || []).slice(0, 6).map((unit) => unit.unitname).filter(Boolean);
    },
    packingSummary() {
      if (this.form.uom_upper && this.form.uom_lower && this.form.conversionfactor) {
        return `1 ${this.form.uom_upper} = ${this.form.conversionfactor} ${this.form.uom_lower}`;
      }
      return "";
    },
    taxReady() {
      if (!this.isTaxableType) return true;
      if (this.form.is_manual_tax_rate) return String(this.form.taxpercent).trim() !== "" && Number(this.form.taxpercent) >= 0;
      return Boolean(String(this.form.hsncode || "").trim()) && String(this.form.taxpercent).trim() !== "";
    },
    checklist() {
      const isNumber = (value) => value !== "" && value !== null && !isNaN(value) && Number(value) >= 0;
      return [
        { label: "Item name", ok: Boolean(this.form.itemname.trim()), focus: "itemNameInput" },
        { label: "Category", ok: Boolean(this.form.itemcategory), focus: "categorySelect" },
        { label: "Item code", ok: Boolean(this.form.itemcode.trim()) && !this.errors.itemcode, focus: "itemcodeInput" },
        { label: "Selling price", ok: isNumber(this.form.unitprice), focus: "unitpriceInput" },
        { label: "Unit", ok: Boolean(this.form.unit), focus: "unitSelect" },
        { label: this.isEdit ? "Current stock" : "Opening stock", ok: this.isEdit || isNumber(this.form.available), focus: "availableInput" },
        { label: "Low-stock alert", ok: isNumber(this.form.minstock), focus: "minstockInput" },
        { label: "Tax", ok: this.taxReady, focus: "taxSection" },
      ];
    },
    checklistDone() {
      return this.checklist.filter((check) => check.ok).length;
    },
    sectionDone() {
      const ok = Object.fromEntries(this.checklist.map((check) => [check.label, check.ok]));
      return {
        basic: ok["Item name"] && ok["Category"] && ok["Item code"],
        pricing: ok["Selling price"],
        stock: ok["Unit"] && ok[this.isEdit ? "Current stock" : "Opening stock"] && ok["Low-stock alert"],
        tax: ok["Tax"],
      };
    },
    priceWithTax() {
      const price = parseFloat(this.form.unitprice) || 0;
      const tax   = parseFloat(this.form.taxpercent) || 0;
      
      if (this.form.istaxinclusive) {
        // Tax is included in price, show tax component
        const taxAmount = (price * tax) / (100 + tax);
        return taxAmount.toFixed(2);
      } else {
        // Tax is added to price
        return (price * tax / 100).toFixed(2);
      }
    },
  },

  created() {
    this.fetchCategoryListFromApi();
    this.fetchUnitListFromApi();
    if (this.isEdit) {
      this.fetchEcommerceCategories();
      this.loadItem();
    }
  },

  mounted() {
    if (!this.isEdit) this.$refs.itemNameInput?.focus();
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },

  methods: {
    // ── Edit mode ──────────────────────────────────────
    snapshot() {
      return JSON.stringify({
        form: this.form,
        images: this.localImages.map((image) => image.url),
      });
    },
    markSaved() {
      this.savedSnapshot = this.snapshot();
    },
    onBeforeUnload(event) {
      if (this.isEdit && this.isDirty && !this.saving) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    async loadItem() {
      if (!this.itemId) {
        this.loadFailed = true;
        this.pageLoading = false;
        return;
      }
      this.pageLoading = true;
      try {
        const { data } = await axios.get(`/v1/items/ecommerce/${this.itemId}`);
        if (!data?.itemid) throw new Error("Item not found");
        this.form = {
          itemname: data.itemname || "",
          itemdescription: data.itemdescription || "",
          itemcategory: data.itemcategory || "",
          itemcode: data.itemcode || "",
          barcode: data.barcode || "",
          rackno: data.rackno || "",
          unitprice: data.unitprice ?? "",
          purchaserate: data.purchaserate ?? "",
          mrp: data.mrp ?? "",
          wholesalerate: data.wholesalerate ?? "",
          unit: data.unit || "",
          available: data.available ?? "",
          threshold: data.threshold ?? "",
          minstock: data.minstock ?? data.threshold ?? "",
          maxstock: data.maxstock ?? "",
          hsncode: data.hsncode || "",
          taxpercent: data.taxpercent ?? "",
          cgstrate: data.cgstrate ?? "",
          sgstrate: data.sgstrate ?? "",
          igstrate: data.igstrate ?? "",
          is_manual_tax_rate: Boolean(data.is_manual_tax_rate),
          taxtype: data.taxtype || "GST",
          istaxinclusive: Boolean(data.istaxinclusive),
          uom_upper: data.uom_upper || "",
          uom_lower: data.uom_lower || "",
          conversionfactor: data.conversionfactor ?? "",
        };
        this.originalItemCode = this.form.itemcode;
        this.showDescription = Boolean(this.form.itemdescription);
        this.showPacking = Boolean(this.form.uom_upper || this.form.uom_lower);

        const ecommerce = data.ecommerce || {};
        this.ecommerceForm = {
          is_online: Boolean(data.is_online || ecommerce.is_active),
          online_price: data.online_price ?? "",
          slug: data.slug || ecommerce.slug || this.slugify(data.itemname || ""),
          short_description: ecommerce.short_description || data.itemdescription || "",
          long_description: ecommerce.long_description || data.online_desc || "",
          seo_title: ecommerce.seo_title || data.itemname || "",
          seo_description: ecommerce.seo_description || "",
          category_ids: (ecommerce.categories || []).map((category) => category.ecommerce_category_id),
        };

        if (this.isTaxableType) {
          const tax = parseFloat(this.form.taxpercent);
          const match = this.gstRates.find((rate) => rate.total === tax);
          this.selectedGstRate = Number.isNaN(tax) ? null : match ? match.label : "custom";
          if (this.form.hsncode && !this.form.is_manual_tax_rate) {
            this.taxRateLookup = {
              status: "found",
              message: `${this.codeLabel} ${this.form.hsncode} currently uses ${this.form.taxpercent}% tax.`,
            };
          }
        }

        await this.loadExistingImages();
        this.markSaved();
      } catch (error) {
        console.error("Error loading item:", error);
        this.loadFailed = true;
      } finally {
        this.pageLoading = false;
      }
    },
    async loadExistingImages() {
      try {
        const response = await axios.get(`/v1/items/${this.itemId}/images`);
        this.localImages = (response.data || []).map((image) => ({
          ...image,
          url: image.preview_url || image.url,
          isNew: false,
        }));
      } catch (error) {
        console.error("Error loading existing images:", error);
      }
    },
    async deleteRemovedImages() {
      for (const image of this.imagesToDelete) {
        await axios.delete(`/v1/items/${this.itemId}/images`, {
          data: { image_key: image.image_key || image.url },
        });
      }
      this.imagesToDelete = [];
    },
    async fetchEcommerceCategories() {
      if (!this.isEcommerceStoreEnabled) return;
      try {
        const response = await axios.get("/v1/ecommerce-categories");
        this.ecommerceCategories = response.data || [];
      } catch (error) {
        console.error("Error fetching ecommerce categories:", error);
      }
    },
    slugify(value) {
      return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    async saveOnlineProduct() {
      this.onlineSaving = true;
      try {
        const payload = {
          ...this.ecommerceForm,
          slug: this.ecommerceForm.slug || this.slugify(this.form.itemname),
          online_price: this.ecommerceForm.online_price === "" ? null : Number(this.ecommerceForm.online_price),
        };
        await axios.put(`/v1/items/${this.itemId}/online`, payload);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Online details saved", showConfirmButton: false, timer: 1600 });
      } catch (error) {
        Swal.fire({ title: "Could not save online details", text: error.response?.data?.detail || error.message, icon: "error" });
      } finally {
        this.onlineSaving = false;
      }
    },
    async removeFromOnlineStore() {
      const result = await Swal.fire({
        icon: "question",
        title: "Remove from online store?",
        text: "Customers will no longer see this item online. Your stock and prices are not affected.",
        showCancelButton: true,
        confirmButtonText: "Yes, remove",
      });
      if (!result.isConfirmed) return;
      this.onlineSaving = true;
      try {
        await axios.put(`/v1/items/${this.itemId}/offline`);
        this.ecommerceForm.is_online = false;
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Removed from online store", showConfirmButton: false, timer: 1600 });
      } catch (error) {
        Swal.fire({ title: "Could not remove item", text: error.response?.data?.detail || error.message, icon: "error" });
      } finally {
        this.onlineSaving = false;
      }
    },

    // ── Small helpers ──────────────────────────────────
    clearError(field) {
      if (this.errors[field]) this.errors = { ...this.errors, [field]: "" };
    },
    statusIcon(status) {
      return { loading: "fa-spinner fa-spin", found: "fa-check-circle", missing: "fa-exclamation-circle" }[status] || "fa-info-circle";
    },
    money(value) {
      return `₹${(Number(value) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatPercent(value) {
      return `${Number(value) || 0}%`;
    },
    onKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (!this.saving && !this.showUnitModal && !this.showTaxRateModal) this.postData();
      }
    },
    goToField(refName) {
      const target = this.$refs[refName];
      const el = target?.$el || target;
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const input = el.matches?.("input, textarea") ? el : el.querySelector?.("input, textarea");
      setTimeout(() => input?.focus(), 250);
    },
    generateItemCode() {
      const source = this.categoryName || this.form.itemname || "IT";
      const prefix = source.replace(/[^a-z]/gi, "").slice(0, 3).toUpperCase() || "IT";
      const suffix = String(Date.now()).slice(-5);
      this.form.itemcode = `${prefix}-${suffix}`;
      this.clearError("itemcode");
      this.checkItemCodeAvailability();
    },
    setTaxType(value) {
      if (this.form.taxtype === value) return;
      this.form.taxtype = value;
      this.handleTaxTypeChange();
    },
    enableManualTax() {
      this.form.is_manual_tax_rate = true;
      this.handleManualTaxToggle();
      this.clearError("hsncode");
    },
    disableManualTax() {
      this.form.is_manual_tax_rate = false;
      this.handleManualTaxToggle();
      this.clearError("taxpercent");
      this.$nextTick(() => this.goToField("hsnInput"));
    },
    onDropImages(event) {
      this.isDragging = false;
      const files = Array.from(event.dataTransfer?.files || []).filter((file) => file.type.startsWith("image/"));
      if (files.length) this.handleImageUpload({ target: { files, value: null } });
    },
    resetForAnother() {
      const keep = {
        itemcategory: this.form.itemcategory,
        unit: this.form.unit,
        taxtype: this.form.taxtype,
        istaxinclusive: this.form.istaxinclusive,
      };
      Object.keys(this.form).forEach((key) => {
        this.form[key] = "";
      });
      Object.assign(this.form, keep, { is_manual_tax_rate: false });
      this.localImages.forEach((img) => {
        if (img?.isNew && img.url) URL.revokeObjectURL(img.url);
      });
      this.localImages = [];
      this.errors = {};
      this.selectedGstRate = null;
      this.showDescription = false;
      this.showPacking = false;
      this.clearItemCodeLookup();
      this.clearTaxRateLookup();
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.$nextTick(() => this.$refs.itemNameInput?.focus());
    },

    // ── Focus helper ───────────────────────────────────
    focusNext(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el && el.$el) el.$el.querySelector("input")?.focus();
        else if (el) el.focus?.();
      });
    },

    // ── Category fetch ─────────────────────────────────
    todayString() {
      const date = new Date();
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },

    clearItemCodeLookup() {
      this.itemCodeLookup = { status: "", message: "" };
    },

    onItemCodeInput() {
      if (this.isEdit && this.form.itemcode.trim() === this.originalItemCode) {
        clearTimeout(this.itemCodeCheckTimer);
        this.clearItemCodeLookup();
        this.clearError("itemcode");
        return;
      }
      if (this.errors.itemcode && this.form.itemcode.trim()) this.errors.itemcode = "";
      clearTimeout(this.itemCodeCheckTimer);
      const code = this.form.itemcode.trim();
      if (!code) {
        this.clearItemCodeLookup();
        return;
      }
      this.itemCodeCheckTimer = setTimeout(() => {
        this.checkItemCodeAvailability();
      }, 450);
    },

    async checkItemCodeAvailability() {
      const code = this.form.itemcode.trim();
      if (this.isEdit && code === this.originalItemCode) {
        this.clearItemCodeLookup();
        return true;
      }
      if (!code) {
        this.clearItemCodeLookup();
        return false;
      }

      this.itemCodeChecking = true;
      this.itemCodeLookup = { status: "loading", message: "Checking item code..." };
      try {
        const response = await axios.get("/v1/items/availability/item-code", {
          params: { itemcode: code, itemid: this.itemId || undefined },
        });
        if (response.data?.exists) {
          const itemName = response.data.itemname ? ` (${response.data.itemname})` : "";
          this.errors.itemcode = `Item code already exists${itemName}.`;
          this.itemCodeLookup = {
            status: "missing",
            message: `Item code "${code}" is already used${itemName}.`,
          };
          return false;
        }
        if (this.errors.itemcode?.startsWith("Item code already exists")) {
          this.errors.itemcode = "";
        }
        this.itemCodeLookup = {
          status: "found",
          message: `Item code "${code}" is available.`,
        };
        return true;
      } catch (error) {
        console.error(error);
        this.itemCodeLookup = {
          status: "missing",
          message: "Could not verify item code right now.",
        };
        return true;
      } finally {
        this.itemCodeChecking = false;
      }
    },

    async fetchCategoryListFromApi() {
      try {
        const response = await axios.get("/v1/category");
        this.categoryListFromApi = response.data;
      } catch (error) {
        console.error("Error fetching category list:", error);
      }
    },

    async fetchUnitListFromApi() {
      try {
        const response = await axios.get("/v1/units");
        this.unitListFromApi = response.data;
      } catch (error) {
        console.error("Error fetching unit list:", error);
      }
    },

    getEmptyUnitModalForm() {
      return {
        unitname: "",
        unitcode: "",
        description: "",
      };
    },

    openUnitModal() {
      this.unitModalForm = this.getEmptyUnitModalForm();
      this.showUnitModal = true;
      this.$nextTick(() => {
        const input = this.$refs.unitModalNameInput;
        input?.focus?.();
      });
    },

    closeUnitModal() {
      this.showUnitModal = false;
      this.unitModalSaving = false;
      this.unitModalForm = this.getEmptyUnitModalForm();
    },

    async createUnitFromModal() {
      const unitname = this.unitModalForm.unitname?.trim();
      if (!unitname) {
        Swal.fire({ icon: "warning", title: "Unit name required", text: "Please enter a unit name." });
        return;
      }

      this.unitModalSaving = true;
      try {
        const payload = {
          unitname,
          unitcode: this.unitModalForm.unitcode?.trim() || null,
          description: this.unitModalForm.description?.trim() || null,
        };
        const response = await axios.post("/v1/units", payload);
        await this.fetchUnitListFromApi();
        this.form.unit = response.data?.unitname || unitname;
        this.errors.unit = "";
        this.closeUnitModal();
        Swal.fire({
          icon: "success",
          title: "Unit added",
          text: `"${unitname}" is ready to use.`,
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Could not add unit",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.unitModalSaving = false;
      }
    },

    // ── Tax type change handler ───────────────────────
    openTaxRateModal() {
      const code = String(this.form.hsncode || "").trim();
      if (!code) {
        this.errors.hsncode = `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} code is required.`;
        return;
      }
      this.taxRateModalForm = {
        code_type: this.form.taxtype === "SAC" ? "SAC" : "HSN",
        code,
        tax_percent: "",
        effective_from: this.todayString(),
      };
      this.showTaxRateModal = true;
      this.$nextTick(() => {
        const input = this.$refs.taxRatePercentInput;
        if (input && input.$el) input.$el.querySelector("input")?.focus();
        else input?.focus?.();
      });
    },

    closeTaxRateModal() {
      this.showTaxRateModal = false;
      this.taxRateModalSaving = false;
      this.taxRateModalForm = {
        code_type: this.form.taxtype === "SAC" ? "SAC" : "HSN",
        code: String(this.form.hsncode || "").trim(),
        tax_percent: "",
        effective_from: this.todayString(),
      };
    },

    async createTaxRateFromModal() {
      const taxPercent = Number(this.taxRateModalForm.tax_percent);
      if (Number.isNaN(taxPercent) || taxPercent < 0 || taxPercent > 100) {
        Swal.fire({ icon: "warning", title: "Invalid tax percent", text: "Enter a tax percent between 0 and 100." });
        return;
      }
      if (!this.taxRateModalForm.effective_from) {
        Swal.fire({ icon: "warning", title: "Effective date required", text: "Please select an effective from date." });
        return;
      }

      this.taxRateModalSaving = true;
      try {
        await axios.post("/v1/tax/rates", {
          code_type: this.taxRateModalForm.code_type,
          code: this.taxRateModalForm.code,
          tax_percent: taxPercent,
          effective_from: this.taxRateModalForm.effective_from,
          effective_to: null,
        });
        this.closeTaxRateModal();
        await this.resolveTaxRateForCode();
        Swal.fire({
          icon: "success",
          title: "Tax rate added",
          text: `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} ${this.form.hsncode} is ready to use.`,
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Could not add tax rate",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.taxRateModalSaving = false;
      }
    },

    handleTaxTypeChange() {
      if (!this.isTaxableType) {
        // Reset tax fields for non-GST items
        this.form.hsncode = "";
        this.form.is_manual_tax_rate = false;
        this.form.taxpercent = "0";
        this.form.cgstrate = "0";
        this.form.sgstrate = "0";
        this.form.igstrate = "0";
        this.selectedGstRate = null;
        this.clearTaxRateLookup();
      } else if (this.form.hsncode && !this.form.is_manual_tax_rate) {
        this.resolveTaxRateForCode();
      }
    },
    handleManualTaxToggle() {
      if (this.form.is_manual_tax_rate) {
        this.form.hsncode = "";
        this.selectedGstRate = "custom";
        this.clearTaxRateLookup();
      } else {
        this.form.taxpercent = "";
        this.form.cgstrate = "";
        this.form.sgstrate = "";
        this.form.igstrate = "";
        this.selectedGstRate = null;
      }
    },

    clearTaxRateLookup() {
      this.taxRateLookup = { status: "", message: "" };
    },

    async resolveTaxRateForCode() {
      if (!this.isTaxableType || this.form.is_manual_tax_rate) return;
      const code = String(this.form.hsncode || "").trim();
      if (!code) {
        this.clearTaxRateLookup();
        return;
      }

      this.taxRateLookup = { status: "loading", message: "Checking HSN Tax Master..." };
      try {
        const response = await axios.get("/v1/tax/rates/resolve", {
          params: {
            code_type: this.form.taxtype === "SAC" ? "SAC" : "HSN",
            code,
          },
        });
        const rate = response.data || {};
        this.form.taxpercent = String(rate.tax_percent ?? "");
        this.form.cgstrate = String(rate.cgstrate ?? "");
        this.form.sgstrate = String(rate.sgstrate ?? "");
        this.form.igstrate = String(rate.igstrate ?? "");
        const match = this.gstRates.find((slab) => Number(slab.total) === Number(rate.tax_percent));
        this.selectedGstRate = match ? match.label : "custom";
        this.taxRateLookup = {
          status: "found",
          message: `${rate.code_type} ${rate.code} is active at ${this.form.taxpercent}% tax.`,
        };
        this.errors.hsncode = "";
      } catch (error) {
        this.form.taxpercent = "";
        this.form.cgstrate = "";
        this.form.sgstrate = "";
        this.form.igstrate = "";
        this.selectedGstRate = null;
        this.taxRateLookup = {
          status: "missing",
          message: error.response?.data?.detail || "No active tax rate found. Add this code in HSN Tax Master first.",
        };
      }
    },

    // ── GST rate quick-apply ───────────────────────────
    applyGstRate(rate) {
      this.selectedGstRate  = rate.label;
      this.form.taxpercent  = rate.total > 0 ? String(rate.total) : "0";
      this.form.cgstrate    = String(rate.cgst);
      this.form.sgstrate    = String(rate.sgst);
      this.form.igstrate    = String(rate.igst);
    },

    // ── Tax field sync ─────────────────────────────────
    syncTaxFromTotal() {
      const total    = parseFloat(this.form.taxpercent) || 0;
      const half     = parseFloat((total / 2).toFixed(2));
      this.form.cgstrate = String(half);
      this.form.sgstrate = String(half);
      this.form.igstrate = String(total);
    },
    syncTaxFromCgst() {
      const cgst           = parseFloat(this.form.cgstrate) || 0;
      const sgst           = parseFloat(this.form.sgstrate) || 0;
      this.form.taxpercent = String(parseFloat((cgst + sgst).toFixed(2)));
      this.form.igstrate = this.form.taxpercent;
    },
    syncTaxFromSgst() {
      const cgst           = parseFloat(this.form.cgstrate) || 0;
      const sgst           = parseFloat(this.form.sgstrate) || 0;
      this.form.taxpercent = String(parseFloat((cgst + sgst).toFixed(2)));
      this.form.igstrate = this.form.taxpercent;
    },
    syncTaxFromIgst() {
      const igst = parseFloat(this.form.igstrate) || 0;
      this.form.taxpercent = String(igst);
      const half = parseFloat((igst / 2).toFixed(2));
      this.form.cgstrate = String(half);
      this.form.sgstrate = String(half);
    },

    // ── IMAGE METHODS ─────────────────────────────────
    handleImageUpload(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;

      const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      const maxSizeBytes = 2 * 1024 * 1024;
      const combinedCount = this.localImages.length + files.length;
      if (combinedCount > this.maxImages) {
        this.uploadError = `You can only upload up to ${this.maxImages} images.`;
        return;
      }

      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          this.uploadError = "Only JPG, JPEG, PNG and WEBP images are allowed.";
          return;
        }
        if (file.size > maxSizeBytes) {
          this.uploadError = "Each image must be 2 MB or smaller.";
          return;
        }
      }

      files.forEach((file) => {
        const url = URL.createObjectURL(file);
        this.localImages.push({ file, url, isNew: true });
      });
      this.uploadError = "";
      event.target.value = null;
    },

    removeLocalImage(index) {
      const image = this.localImages[index];
      if (image && !image.isNew) this.imagesToDelete.push(image);
      if (image?.url && image.isNew) {
        URL.revokeObjectURL(image.url);
      }
      this.localImages.splice(index, 1);
      this.uploadError = "";
    },

    async uploadImages(itemId) {
      const files = this.localImages.filter((img) => img.isNew).map((img) => img.file);
      if (!files.length) return [];
      return await ImageUploadApi(itemId, files);
    },

    // ── Validation ─────────────────────────────────────
    validate() {
      this.errors = {};
      if (!this.form.itemname.trim())
        this.errors.itemname      = "Item name is required.";
      if (!this.form.itemcode.trim())
        this.errors.itemcode      = "Item code is required.";
      if (!this.form.itemcategory)
        this.errors.itemcategory  = "Please select a category.";
      if (!this.form.unit.trim())
        this.errors.unit          = "Unit is required.";
      if (this.form.unitprice === "" || isNaN(this.form.unitprice) || Number(this.form.unitprice) < 0)
        this.errors.unitprice     = "Enter a valid unit price.";
      if (!this.isEdit && (this.form.available === "" || isNaN(this.form.available) || Number(this.form.available) < 0))
        this.errors.available     = "Enter a valid available quantity.";
      if (this.form.minstock === "" || isNaN(this.form.minstock) || Number(this.form.minstock) < 0)
        this.errors.minstock      = "Enter a valid minimum stock value.";
      if (this.isTaxableType && !this.form.is_manual_tax_rate && !String(this.form.hsncode || "").trim())
        this.errors.hsncode       = `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} code is required unless manual tax rate is enabled.`;
      if (this.isTaxableType && !this.form.is_manual_tax_rate && String(this.form.hsncode || "").trim() && !String(this.form.taxpercent || "").trim())
        this.errors.hsncode       = `${this.form.taxtype === "SAC" ? "SAC" : "HSN"} code must exist in HSN Tax Master.`;
      if (this.isTaxableType && this.form.is_manual_tax_rate && (this.form.taxpercent === "" || isNaN(this.form.taxpercent) || Number(this.form.taxpercent) < 0))
        this.errors.taxpercent    = "Enter a valid manual tax percent.";
      return Object.keys(this.errors).length === 0;
    },

    // ── Submit ─────────────────────────────────────────
    async postData({ addAnother = false } = {}) {
      if (this.saving) return;
      if (!this.validate()) {
        const missing = Object.values(this.errors).filter(Boolean).length;
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: `Please fix ${missing} field${missing === 1 ? "" : "s"}`,
          showConfirmButton: false,
          timer: 2200,
        });
        this.$nextTick(() => {
          const el = document.querySelector(".ap-main .is-invalid");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        return;
      }

      this.saving = true;
      try {
        // Build clean payload — omit empty optional fields
        const payload = {};
        Object.entries(this.form).forEach(([key, val]) => {
          if (val !== "" && val !== null && val !== undefined) {
            payload[key] = val;
          }
        });
        payload.threshold = Number(this.form.minstock || 0);
        payload.maxstock = this.isEdit && this.form.maxstock !== "" && this.form.maxstock !== null ? Number(this.form.maxstock) : null;

        let savedItemId = this.itemId;
        if (this.isEdit) {
          await axios.put(`/v1/items/${this.itemId}`, payload);
        } else {
          const itemResponse = await axios.post("/v1/items", payload);
          savedItemId = itemResponse.data.itemid;
        }
        let imageError = "";
        if (savedItemId) {
          try {
            if (this.isEdit) await this.deleteRemovedImages();
            if (this.localImages.some((image) => image.isNew)) await this.uploadImages(savedItemId);
          } catch (error) {
            imageError = error.response?.data?.detail || error.message;
          }
        }

        const name = this.form.itemname;
        if (imageError) {
          await Swal.fire({ icon: "warning", title: "Item saved, but photos were not updated", text: String(imageError) });
        } else {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: this.isEdit ? `"${name}" updated` : `"${name}" saved`,
            showConfirmButton: false,
            timer: 1800,
          });
        }

        if (this.isEdit) {
          this.markSaved();
        }
        if (addAnother) {
          this.resetForAnother();
        } else {
          this.$router.push({ path: "/productlist" });
        }
      } catch (error) {
        console.error(error);
        const msg = error.response?.data?.detail || error.message;
        Swal.fire({ title: this.isEdit ? "Could not update item" : "Could not save item", text: typeof msg === "string" ? msg : JSON.stringify(msg), icon: "error" });
      } finally {
        this.saving = false;
      }
    },
  },

  // beforeDestroy() {
  //   Image cleanup disabled — future feature
  //   this.localImages.forEach((img) => URL.revokeObjectURL(img.url));
  // },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    clearTimeout(this.itemCodeCheckTimer);
    this.localImages.forEach((img) => {
      if (img?.isNew && img.url) URL.revokeObjectURL(img.url);
    });
  },
};
</script>

<style scoped>
/* ── layout ─────────────────────────────────────── */
.ap-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 18px;
  align-items: start;
}
.ap-main { display: flex; flex-direction: column; gap: 16px; min-width: 0; }

.ap-card {
  background: #fff;
  border: 1px solid #e9ecf2;
  border-radius: 12px;
  padding: 18px 20px 20px;
}
.ap-card-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}
.ap-card-head h6 { margin: 2px 0 2px; font-size: 15px; font-weight: 700; color: #111827; }
.ap-card-head p { margin: 0; font-size: 12.5px; color: #6b7280; }
.ap-step {
  flex: 0 0 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff4e8;
  color: #c2410c;
  font-size: 13px;
  font-weight: 700;
  transition: background 0.2s ease, color 0.2s ease;
}
.ap-step.done { background: #dcfce7; color: #15803d; }
.ap-step.muted { background: #f3f4f6; color: #6b7280; }

/* ── form controls ──────────────────────────────── */
.form-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
.req { color: #dc2626; }
.required-star { color: #e53935; font-weight: 700; }
.opt { font-size: 11px; font-weight: 400; color: #9ca3af; margin-left: 2px; }
.ap-help { display: block; margin-top: 5px; font-size: 12px; color: #9ca3af; }
.ap-main :deep(.form-control),
.ap-main :deep(.custom-select),
.ap-main :deep(.ui.search.selection.dropdown) {
  min-height: 40px;
  border-radius: 8px;
  border-color: #e5e7eb;
  font-size: 14px;
}
.ap-main :deep(.form-control:focus) {
  border-color: #FF9F43;
  box-shadow: 0 0 0 3px rgba(255, 159, 67, 0.15);
}
.ap-main :deep(.is-invalid.ui.dropdown),
.ap-main :deep(.is-invalid .ui.dropdown) { border-color: #dc3545 !important; }

.ap-input-action { display: flex; gap: 6px; }
.ap-input-action > :first-child { flex: 1; min-width: 0; }
.ap-inline-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 12px;
  min-height: 40px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  background: #fffaf5;
  color: #c2410c;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
}
.ap-inline-btn:hover { background: #fff1e0; }

.ap-icon-input { position: relative; }
.ap-icon-input > i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; font-size: 13px; z-index: 1; }
.ap-icon-input :deep(.form-control) { padding-left: 34px; }

.ap-money { position: relative; }
.ap-money > span { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #6b7280; font-weight: 600; z-index: 1; }
.ap-money :deep(.form-control) { padding-left: 28px; font-variant-numeric: tabular-nums; }

.ap-suffix { position: relative; }
.ap-suffix > span {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  color: #9ca3af; font-size: 12.5px; pointer-events: none; max-width: 70px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ap-suffix :deep(.form-control) { padding-right: 70px; }

.ap-unit-row { display: flex; gap: 6px; }
.ap-unit-row > :first-child { flex: 1; min-width: 0; }

.ap-status { margin-top: 6px; font-size: 12px; display: flex; align-items: center; gap: 6px; }
.ap-status.loading { color: #6b7280; }
.ap-status.found { color: #15803d; }
.ap-status.missing { color: #dc2626; }

.ap-link-btn {
  border: 0;
  background: transparent;
  padding: 0;
  color: #c2410c;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ap-link-btn:hover { text-decoration: underline; }
.ap-link-btn.muted { color: #6b7280; font-weight: 500; }

.ap-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.ap-chip {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  color: #374151;
}
.ap-chip:hover { border-color: #FF9F43; color: #c2410c; }

.ap-hints { display: flex; flex-direction: column; gap: 6px; margin-top: 14px; }
.ap-hint { display: flex; align-items: flex-start; gap: 8px; padding: 9px 12px; border-radius: 8px; font-size: 12.5px; }
.ap-hint i { margin-top: 2px; }
.ap-hint.warn { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
.ap-hint.info { background: #f0f9ff; color: #075985; border: 1px solid #bae6fd; }

/* ── packing units ──────────────────────────────── */
.ap-sub { margin-top: 16px; border: 1px dashed #e5e7eb; border-radius: 10px; }
.ap-sub.open { border-style: solid; background: #fcfcfd; }
.ap-sub-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border: 0;
  background: transparent;
  text-align: left;
  color: #374151;
}
.ap-sub-toggle > i:first-child { color: #9ca3af; }
.ap-sub-toggle span { flex: 1; display: flex; flex-direction: column; }
.ap-sub-toggle strong { font-size: 13px; }
.ap-sub-toggle small { font-size: 12px; color: #6b7280; }
.ap-chevron { color: #9ca3af; transition: transform 0.2s ease; }
.ap-sub.open .ap-chevron { transform: rotate(180deg); }
.ap-packing { padding: 0 14px 14px; }
.ap-packing-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ap-packing-one, .ap-packing-eq { font-weight: 700; color: #6b7280; }
.ap-packing-name { flex: 1 1 140px; max-width: 200px; }
.ap-packing-qty { flex: 0 0 90px; }

/* ── tax ────────────────────────────────────────── */
.ap-segment {
  display: inline-flex;
  flex-wrap: wrap;
  padding: 3px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
  gap: 2px;
}
.ap-segment button {
  border: 0;
  background: transparent;
  padding: 7px 14px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}
.ap-segment button.active { background: #fff; color: #c2410c; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1); }
.ap-segment-sm button { padding: 6px 12px; }
.ap-rates { display: flex; flex-wrap: wrap; gap: 6px; }
.ap-rates button {
  min-width: 56px;
  padding: 8px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  font-weight: 700;
  font-size: 13.5px;
  color: #374151;
}
.ap-rates button:hover { border-color: #fdba74; }
.ap-rates button.active { border-color: #FF9F43; background: #fff4e8; color: #c2410c; }
.ap-tax-links { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; margin-top: 8px; }
.ap-tax-split {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f9fafb;
  font-weight: 700;
  color: #111827;
  font-variant-numeric: tabular-nums;
}
.ap-tax-split span { display: inline-flex; flex-direction: column; }
.ap-tax-split small { font-size: 11px; font-weight: 500; color: #6b7280; }
.ap-tax-split .ap-tax-plus, .ap-tax-split .ap-tax-or { color: #9ca3af; font-weight: 500; font-size: 12px; }

/* ── photos ─────────────────────────────────────── */
.ap-drop {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px;
  border: 1.5px dashed #e5e7eb;
  border-radius: 12px;
  background: #fafafa;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.ap-drop.dragging { border-color: #FF9F43; background: #fff8f0; }
.ap-thumb { position: relative; width: 96px; height: 96px; border-radius: 10px; overflow: hidden; border: 1px solid #e5e7eb; background: #fff; }
.ap-thumb img { width: 100%; height: 100%; object-fit: cover; }
.ap-thumb-tag {
  position: absolute; left: 6px; bottom: 6px; padding: 1px 6px; border-radius: 4px;
  background: rgba(17, 24, 39, 0.75); color: #fff; font-size: 10px; font-weight: 600;
}
.ap-thumb-remove {
  position: absolute; top: 5px; right: 5px; width: 22px; height: 22px; border: 0; border-radius: 50%;
  background: rgba(17, 24, 39, 0.7); color: #fff; font-size: 11px; display: flex; align-items: center; justify-content: center;
}
.ap-thumb-remove:hover { background: #dc2626; }
.ap-drop-add {
  flex: 1 1 180px;
  min-height: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #6b7280;
  font-size: 13px;
}
.ap-drop-add i { font-size: 22px; color: #FF9F43; }
.ap-drop-add:hover { color: #c2410c; }

/* ── summary ────────────────────────────────────── */
.ap-side { position: sticky; top: 80px; }
.ap-summary {
  background: #fff;
  border: 1px solid #e9ecf2;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.ap-preview { display: flex; align-items: center; gap: 12px; }
.ap-preview-img {
  flex: 0 0 52px; height: 52px; border-radius: 10px; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #fff4e8, #ffe2c4); color: #c2410c; font-weight: 700; font-size: 18px;
}
.ap-preview-img img { width: 100%; height: 100%; object-fit: cover; }
.ap-preview-text { min-width: 0; display: flex; flex-direction: column; }
.ap-preview-text strong { font-size: 15px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ap-preview-text small { font-size: 12px; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.ap-figures { margin: 0; padding: 10px 12px; border-radius: 10px; background: #f9fafb; }
.ap-figures > div { display: flex; justify-content: space-between; gap: 10px; padding: 4px 0; font-size: 13px; }
.ap-figures dt { font-weight: 400; color: #6b7280; }
.ap-figures dd { margin: 0; font-weight: 600; color: #111827; font-variant-numeric: tabular-nums; text-align: right; }
.ap-figures dd small { font-weight: 500; }
.ap-figures dd.pos { color: #15803d; }
.ap-figures dd.neg { color: #dc2626; }
.ap-figure-strong { border-top: 1px dashed #e5e7eb; margin-top: 4px; padding-top: 8px !important; }
.ap-figure-strong dt, .ap-figure-strong dd { font-size: 14px; font-weight: 700 !important; color: #111827 !important; }

.ap-checklist-head { display: flex; justify-content: space-between; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.ap-progress { height: 5px; border-radius: 999px; background: #f1f2f4; margin: 6px 0 8px; overflow: hidden; }
.ap-progress span { display: block; height: 100%; background: #22c55e; border-radius: 999px; transition: width 0.25s ease; }
.ap-checklist ul { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 4px 8px; }
.ap-checklist li { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #6b7280; }
.ap-checklist li i { font-size: 11px; color: #d1d5db; }
.ap-checklist li.ok { color: #374151; }
.ap-checklist li.ok i { color: #22c55e; }
.ap-checklist li button { border: 0; background: transparent; padding: 0; color: inherit; text-align: left; }
.ap-checklist li:not(.ok) button:hover { color: #c2410c; text-decoration: underline; }

.ap-actions { display: flex; flex-direction: column; gap: 8px; align-items: stretch; }
.ap-actions .btn { min-height: 42px; font-weight: 600; }
.ap-btn-light { border: 1px solid #e5e7eb; background: #fff; color: #374151; }
.ap-btn-light:hover { border-color: #FF9F43; color: #c2410c; }
.ap-cancel { text-align: center; font-size: 13px; color: #6b7280; padding: 4px; }
.ap-cancel:hover { color: #dc2626; }
.ap-kbd { text-align: center; font-size: 11.5px; color: #9ca3af; }
.ap-kbd kbd { padding: 1px 5px; border-radius: 4px; background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; font-size: 11px; }

/* ── edit mode ──────────────────────────────────── */
.ap-loading { min-height: 220px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #6b7280; font-size: 14px; }
.ap-loading strong { color: #111827; font-size: 16px; }
.ap-spinner { width: 32px; height: 32px; border: 3px solid #f3f4f6; border-top-color: #FF9F43; border-radius: 50%; animation: ap-spin 0.7s linear infinite; }
@keyframes ap-spin { to { transform: rotate(360deg); } }
.ap-main :deep(.ap-readonly) { background: #f9fafb; color: #4b5563; cursor: not-allowed; }
.ap-dirty { text-align: center; font-size: 12px; color: #9ca3af; display: flex; align-items: center; justify-content: center; gap: 6px; }
.ap-dirty i { font-size: 8px; }
.ap-dirty.on { color: #c2410c; }
.ap-online-badge { flex: 0 0 auto; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; background: #f3f4f6; color: #6b7280; }
.ap-online-badge.on { background: #dcfce7; color: #15803d; }
.ap-multi { min-height: 96px !important; }
.ap-online-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px; }

/* ── quick-create modals (unit / tax rate) ─────── */
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.quick-create-modal-backdrop {
  position: fixed; inset: 0; z-index: 1050; background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.quick-create-modal { width: min(100%, 560px); background: #fff; border-radius: 14px; padding: 20px; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18); }
.quick-create-modal-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.quick-create-modal-title { margin: 0; font-size: 1.05rem; font-weight: 700; color: #0f172a; }
.quick-create-modal-subtitle { margin: 4px 0 0; color: #64748b; font-size: 0.88rem; }
.quick-create-close { width: 34px; height: 34px; border: 0; border-radius: 8px; background: #f1f5f9; color: #475569; cursor: pointer; }

/* ── responsive ─────────────────────────────────── */
@media (max-width: 1199px) {
  .ap-layout { grid-template-columns: 1fr; }
  .ap-side { position: static; order: 2; }
  .ap-summary { display: grid; grid-template-columns: 1fr 1fr; align-items: start; }
  .ap-preview, .ap-actions { grid-column: 1 / -1; }
  .ap-actions { flex-direction: row; flex-wrap: wrap; }
  .ap-actions .btn { flex: 1 1 200px; width: auto !important; }
  .ap-cancel, .ap-kbd { flex-basis: 100%; }
}
@media (max-width: 767px) {
  .ap-card { padding: 14px; }
  .ap-summary { display: flex; align-items: stretch; }
  .ap-side { position: sticky; bottom: 0; top: auto; z-index: 5; }
  .ap-side .ap-figures, .ap-side .ap-checklist, .ap-side .ap-preview, .ap-kbd { display: none; }
  .ap-summary { padding: 10px; box-shadow: 0 -6px 18px rgba(15, 23, 42, 0.08); }
  .ap-actions { flex-direction: row; gap: 8px; }
  .ap-actions .btn { flex: 1 1 0; min-width: 0; padding-left: 8px; padding-right: 8px; font-size: 13px; }
  .ap-cancel { display: none; }
}
</style>
